// IABF free-gift catalog and returning-dancer entitlement.
// Year-agnostic rules: gift count is based on attendance year number.

import { IABF_DANCER_YEAR } from './iabf-event'

/** Selectable free gifts for the current festival (2027 catalog). */
export const REGISTRATION_GIFT_OPTIONS = [
  'Free Indoor or Outdoor photoshoot with 2 edited photos',
  '1-Way Airport Carpool (MCO) or 2-way Daytona (DAB)',
  '$50 discount on your final IABF Payment',
  '$75 Merch Credit for IABF Merch or Magazine Copies',
  'Awards Banquet Ticket for 1 Guest',
  'Welcome Dinner Ticket for 1 Guest'
]

/** Admin dropdowns also allow clearing / pending. */
export const REGISTRATION_GIFT_ADMIN_OPTIONS = [
  ...REGISTRATION_GIFT_OPTIONS,
  'None',
  'Not Yet Selected'
]

const GIFT_IGNORE = new Set(['None', '', 'Not Yet Selected', null, undefined])

/**
 * How many free gifts a dancer earns for this festival.
 * 1st year (first time): 0
 * 2nd year: 1 · 3rd: 2 · 4th: 3 · 5th+: 4
 *
 * @param {number[]|undefined} yearsAttended - years they actually attended
 *   (canceled-only years must already be excluded)
 * @param {number} [currentYear=IABF_DANCER_YEAR]
 * @returns {number} 0–4
 */
export function freeGiftCountFromYearsAttended (yearsAttended, currentYear = IABF_DANCER_YEAR) {
  const y = Number(currentYear) || IABF_DANCER_YEAR
  const set = new Set()
  for (const raw of yearsAttended || []) {
    const n = Number(raw)
    if (Number.isFinite(n) && n > 0) {
      set.add(n)
    }
  }
  // Count this festival year even if not yet written on the person doc
  set.add(y)
  const yearNumber = set.size
  if (yearNumber <= 1) {
    return 0
  }
  return Math.min(4, yearNumber - 1)
}

/**
 * Human label for how many gifts they get.
 * @param {number} count
 */
export function freeGiftCountLabel (count) {
  const n = Number(count) || 0
  if (n <= 0) {
    return ''
  }
  if (n === 1) {
    return '1 free gift'
  }
  return `${n} free gifts`
}

/**
 * All selected gift fields on a dancer (slots 1–4 + legacy early bird if any).
 * @param {object} dancer
 * @returns {string[]}
 */
export function selectedGiftsFromDancer (dancer) {
  const d = dancer || {}
  const slots = [
    d.registrationGift1,
    d.registrationGift2,
    d.registrationGift3,
    d.registrationGift4,
    d.earlyBirdGift
  ]
  return slots.filter(g => g && !GIFT_IGNORE.has(g))
}

/**
 * True if dancer has any of the given gift labels (exact match).
 * @param {object} dancer
 * @param {...string} labels
 */
export function dancerHasAnyGift (dancer, ...labels) {
  const selected = selectedGiftsFromDancer(dancer)
  return labels.some(l => selected.includes(l))
}

/** Photoshoot base gift (2 edited photos) — current + legacy labels. */
export const PHOTOSHOOT_GIFT_LABELS = [
  'Free Indoor or Outdoor photoshoot with 2 edited photos',
  '15-min Photoshoot with 2 edited photos'
]

/** Extra photos add-on gift — current + legacy. */
export const PHOTOSHOOT_EXTRA_GIFT_LABELS = [
  '3 extra edited photos for your photoshoot',
  '15-min Photoshoot with 5 edited photos'
]

export const WELCOME_DINNER_GIFT_LABELS = [
  'Welcome Dinner Ticket for 1 Guest',
  'Welcome Dinner for 1 Guest'
]

export const AWARDS_BANQUET_GIFT_LABELS = [
  'Awards Banquet Ticket for 1 Guest'
]

export const MERCH_CREDIT_GIFT_LABELS = [
  '$75 Merch Credit for IABF Merch or Magazine Copies',
  '$100 IABF Merch & Magazine Issue #5 Credit'
]

export const PAYMENT_DISCOUNT_GIFT_LABELS = [
  '$50 discount on your final IABF Payment'
]

/** One gift = 1 free MCO leg, or a full DAB roundtrip. */
export const CARPOOL_ONE_WAY_GIFT_LABEL =
  '1-Way Airport Carpool (MCO) or 2-way Daytona (DAB)'

/** Legacy gift: full free roundtrip (counts as 2 MCO-leg credits). */
export const CARPOOL_ROUNDTRIP_GIFT_LABEL =
  'Roundtrip Airport Carpool (Orlando and Daytona)'

export const CARPOOL_GIFT_LABELS = [
  CARPOOL_ONE_WAY_GIFT_LABEL,
  CARPOOL_ROUNDTRIP_GIFT_LABEL
]

/** 2027 per-leg carpool prices (dollars). SFB is not offered. */
export const CARPOOL_PRICES = {
  MCO: 60,
  DAB: 25
}

/**
 * How many carpool gift credits a dancer has.
 * - Current "1-Way MCO or 2-way DAB" gift → 1 credit each
 * - Legacy "Roundtrip Airport Carpool" → 2 credits each (full MCO RT)
 *
 * @param {object} dancer
 * @returns {number}
 */
export function carpoolGiftCreditsFromDancer (dancer) {
  if (dancer && (dancer.isFaculty || dancer.personType === 'faculty')) {
    return 2
  }
  let credits = 0
  for (const g of selectedGiftsFromDancer(dancer)) {
    if (g === CARPOOL_ROUNDTRIP_GIFT_LABEL) {
      credits += 2
    } else if (g === CARPOOL_ONE_WAY_GIFT_LABEL) {
      credits += 1
    }
  }
  return credits
}

/**
 * Which carpool legs are free under the dancer's gift credits.
 *
 * Rules:
 * - DAB only (arrival and/or departure): 1 credit covers every DAB leg
 * - Otherwise (MCO or mixed): each credit frees one leg (arrival first)
 *
 * @param {object} opts
 * @param {string|null} [opts.arrivalAirport] - e.g. 'MCO' / 'DAB'
 * @param {string|null} [opts.departureAirport]
 * @param {boolean} [opts.hasArrival]
 * @param {boolean} [opts.hasDeparture]
 * @param {number} credits
 * @returns {{ freeArrival: boolean, freeDeparture: boolean, freeBoth: boolean, creditsUsed: number }}
 */
export function carpoolFreeLegs ({
  arrivalAirport = null,
  departureAirport = null,
  hasArrival = false,
  hasDeparture = false,
  credits = 0
} = {}) {
  const free = { freeArrival: false, freeDeparture: false, freeBoth: false, creditsUsed: 0 }
  const n = Math.max(0, Number(credits) || 0)
  if (n <= 0 || (!hasArrival && !hasDeparture)) {
    return free
  }

  const legs = []
  if (hasArrival) {
    legs.push({ direction: 'arrival', airport: String(arrivalAirport || '').toUpperCase() })
  }
  if (hasDeparture) {
    legs.push({ direction: 'departure', airport: String(departureAirport || '').toUpperCase() })
  }

  // Pure DAB trip: one gift covers the whole thing (1-way or roundtrip).
  const allDab = legs.every(l => l.airport === 'DAB')
  if (allDab) {
    free.freeArrival = hasArrival
    free.freeDeparture = hasDeparture
    free.freeBoth = hasArrival && hasDeparture
    free.creditsUsed = 1
    return free
  }

  // MCO (or mixed): one credit per leg.
  let remaining = n
  for (const leg of legs) {
    if (remaining <= 0) {
      break
    }
    if (leg.direction === 'arrival') {
      free.freeArrival = true
    } else {
      free.freeDeparture = true
    }
    remaining -= 1
    free.creditsUsed += 1
  }
  free.freeBoth = free.freeArrival && free.freeDeparture && hasArrival && hasDeparture
  return free
}

/**
 * Dollar total after applying carpool gift credits to booked legs.
 *
 * @param {object} opts
 * @param {{ airport?: string, pricePerPerson?: number, isWaitlist?: boolean }|null} [opts.arrivalSlot]
 * @param {{ airport?: string, pricePerPerson?: number, isWaitlist?: boolean }|null} [opts.departureSlot]
 * @param {number} credits
 * @returns {{ total: number, gross: number, freeArrival: boolean, freeDeparture: boolean, freeBoth: boolean }}
 */
export function carpoolPriceAfterGift ({ arrivalSlot = null, departureSlot = null, credits = 0 } = {}) {
  const arr = arrivalSlot && !arrivalSlot.isWaitlist ? arrivalSlot : null
  const dep = departureSlot && !departureSlot.isWaitlist ? departureSlot : null
  const coverage = carpoolFreeLegs({
    arrivalAirport: arr ? arr.airport : null,
    departureAirport: dep ? dep.airport : null,
    hasArrival: !!arr,
    hasDeparture: !!dep,
    credits
  })
  let gross = 0
  let total = 0
  if (arr) {
    const p = Number(arr.pricePerPerson) || 0
    gross += p
    if (!coverage.freeArrival) {
      total += p
    }
  }
  if (dep) {
    const p = Number(dep.pricePerPerson) || 0
    gross += p
    if (!coverage.freeDeparture) {
      total += p
    }
  }
  return {
    total,
    gross,
    freeArrival: coverage.freeArrival,
    freeDeparture: coverage.freeDeparture,
    freeBoth: coverage.freeBoth
  }
}

/**
 * Gift credit (cents) for an admin add-on carpool variant.
 * - DAB (any): 1 gift credit → full free
 * - MCO one-way: 1 gift credit → full free
 * - MCO roundtrip: 2 gift credits → full free; 1 credit → half (one way)
 *
 * @param {string} variantKey - e.g. 'mco-roundtrip', 'dab-arrival'
 * @param {number} basePriceCents
 * @param {number} remainingCredits - from carpoolGiftCreditsFromDancer (minus already used)
 * @returns {{ giftCreditCents: number, creditsUsed: number }}
 */
export function carpoolAddonGiftCredit (variantKey, basePriceCents, remainingCredits) {
  const key = String(variantKey || '').toLowerCase()
  const base = Math.max(0, Number(basePriceCents) || 0)
  const rem = Math.max(0, Number(remainingCredits) || 0)
  if (!key || rem <= 0 || base <= 0) {
    return { giftCreditCents: 0, creditsUsed: 0 }
  }
  if (key.startsWith('sfb-')) {
    return { giftCreditCents: 0, creditsUsed: 0 }
  }
  if (key.startsWith('dab-')) {
    return { giftCreditCents: base, creditsUsed: 1 }
  }
  if (key === 'mco-roundtrip') {
    if (rem >= 2) {
      return { giftCreditCents: base, creditsUsed: 2 }
    }
    // One gift covers one way of the roundtrip.
    return { giftCreditCents: Math.round(base / 2), creditsUsed: 1 }
  }
  if (key.startsWith('mco-')) {
    return { giftCreditCents: base, creditsUsed: 1 }
  }
  return { giftCreditCents: 0, creditsUsed: 0 }
}

// ── Gift redemption / edit locks ──────────────────────────────────────────
// Prevents double-dipping: once a gift has been used (free carpool, merch
// credit spent, guest ticket gift, photoshoot credit, …), that selection
// cannot be swapped for a different gift in self-serve UI.

/** Logical gift kinds used for redemption accounting. */
export const GIFT_KINDS = [
  'carpool',
  'merch',
  'welcome',
  'banquet',
  'payment',
  'photoshoot',
  'photoshootExtra'
]

const EMPTY_REDEMPTIONS = () => ({
  carpool: 0,
  merch: 0,
  welcome: 0,
  banquet: 0,
  payment: 0,
  photoshoot: 0,
  photoshootExtra: 0
})

const REDEMPTION_REASONS = {
  carpool: 'Already applied to your airport carpool',
  merch: 'Merch credit already used on a store order',
  welcome: 'Already applied to a guest Welcome Dinner',
  banquet: 'Already applied to a guest Awards Banquet ticket',
  payment: 'Already applied to your festival payment',
  photoshoot: 'Already applied to a photoshoot signup',
  photoshootExtra: 'Already applied to extra photoshoot photos'
}

const REDEMPTION_BLOCK_MESSAGES = {
  carpool: 'Your carpool gift is already in use on a booked carpool. Email hello@iabf.dance if you need to switch.',
  merch: 'Your merch credit gift has already been used on a store order. Email hello@iabf.dance if you need help.',
  welcome: 'Your Welcome Dinner gift is already applied to a guest. Email hello@iabf.dance to change it.',
  banquet: 'Your Awards Banquet gift is already applied to a guest. Email hello@iabf.dance to change it.',
  payment: 'Your payment discount gift has already been applied. Email hello@iabf.dance to change it.',
  photoshoot: 'Your photoshoot gift is already applied to a signup. Email hello@iabf.dance to change it.',
  photoshootExtra: 'Your extra-photos gift is already applied. Email hello@iabf.dance to change it.'
}

/**
 * Map a gift label to its accounting kind (or null if not a real gift).
 * @param {string} label
 * @returns {string|null}
 */
export function giftKindFromLabel (label) {
  if (!label || GIFT_IGNORE.has(label)) {
    return null
  }
  if (CARPOOL_GIFT_LABELS.includes(label)) {
    return 'carpool'
  }
  if (MERCH_CREDIT_GIFT_LABELS.includes(label)) {
    return 'merch'
  }
  if (WELCOME_DINNER_GIFT_LABELS.includes(label)) {
    return 'welcome'
  }
  if (AWARDS_BANQUET_GIFT_LABELS.includes(label)) {
    return 'banquet'
  }
  if (PAYMENT_DISCOUNT_GIFT_LABELS.includes(label)) {
    return 'payment'
  }
  if (PHOTOSHOOT_GIFT_LABELS.includes(label)) {
    return 'photoshoot'
  }
  if (PHOTOSHOOT_EXTRA_GIFT_LABELS.includes(label)) {
    return 'photoshootExtra'
  }
  return null
}

/**
 * How many redemption units one selection of this label counts as.
 * Legacy roundtrip carpool = 2 units; everything else = 1.
 * @param {string} label
 * @returns {number}
 */
export function giftUnitsFromLabel (label) {
  if (!label || GIFT_IGNORE.has(label)) {
    return 0
  }
  if (label === CARPOOL_ROUNDTRIP_GIFT_LABEL) {
    return 2
  }
  return giftKindFromLabel(label) ? 1 : 0
}

/**
 * Count selected gift units by kind from a list of slot labels.
 * @param {string[]} slots
 * @returns {Record<string, number>}
 */
export function countGiftUnitsByKind (slots) {
  const counts = EMPTY_REDEMPTIONS()
  for (const label of slots || []) {
    const kind = giftKindFromLabel(label)
    if (!kind) {
      continue
    }
    counts[kind] += giftUnitsFromLabel(label)
  }
  return counts
}

/**
 * Detect how many gift units of each kind have already been redeemed.
 *
 * @param {object} opts
 * @param {object} [opts.dancer]
 * @param {Array} [opts.carpoolSlots] - active carpool slot docs
 * @param {Array} [opts.guestRegistrations]
 * @param {Array} [opts.addonServices] - addon-services rows for this dancer
 * @returns {Record<string, number>}
 */
export function detectGiftRedemptions ({
  dancer = null,
  carpoolSlots = [],
  guestRegistrations = [],
  addonServices = []
} = {}) {
  const d = dancer || {}
  const dancerId = d.id || null
  const out = EMPTY_REDEMPTIONS()

  // Carpool: free legs on booked non-waitlist slots (or addon carpool credit).
  if (dancerId) {
    let arrival = null
    let departure = null
    for (const s of carpoolSlots || []) {
      if (!s || s.isWaitlist || s.active === false) {
        continue
      }
      const onSlot = (s.dancers || []).some(x => x && x.id === dancerId)
      if (!onSlot) {
        continue
      }
      if (s.direction === 'departure') {
        departure = s
      } else {
        arrival = s
      }
    }
    if (!arrival && d.carpoolArrivalSlotId) {
      arrival = (carpoolSlots || []).find(s =>
        s && s.id === d.carpoolArrivalSlotId && !s.isWaitlist
      ) || null
    }
    if (!departure && d.carpoolDepartureSlotId) {
      departure = (carpoolSlots || []).find(s =>
        s && s.id === d.carpoolDepartureSlotId && !s.isWaitlist
      ) || null
    }
    const credits = carpoolGiftCreditsFromDancer(d)
    if (credits > 0 && (arrival || departure)) {
      const coverage = carpoolFreeLegs({
        arrivalAirport: arrival ? arrival.airport : null,
        departureAirport: departure ? departure.airport : null,
        hasArrival: !!arrival,
        hasDeparture: !!departure,
        credits
      })
      if (coverage.freeArrival || coverage.freeDeparture) {
        out.carpool += coverage.creditsUsed
      }
    }
  }

  // Merch credit spent on store orders (tracked on the dancer doc).
  const merchUsed = Number(d.merchCreditUsedCents) || 0
  if (merchUsed > 0) {
    // Each merch gift is $75 (legacy $100). Count units conservatively by $75.
    out.merch = Math.max(1, Math.ceil(merchUsed / 7500))
  }

  // Guest ticket gifts — flags set when the guest is registered.
  for (const g of guestRegistrations || []) {
    if (g && g.usedWelcomeDinnerGift) {
      out.welcome += 1
    }
    if (g && g.usedBanquetGift) {
      out.banquet += 1
    }
    // allMeals without explicit flags: if price looks discounted and dancer
    // currently holds the gift, we still only trust the flags (fixed on write).
  }

  // Payment discount — only when explicitly marked applied on the dancer.
  if (d.paymentDiscountApplied || d.giftPaymentDiscountApplied) {
    out.payment = Math.max(out.payment, 1)
  }

  // Addon services (photoshoot / carpool credits from admin billing).
  for (const r of addonServices || []) {
    if (!r || !r.giftCreditCents) {
      continue
    }
    if (dancerId && r.dancerId && r.dancerId !== dancerId) {
      continue
    }
    if (r.serviceType === 'photoshoot') {
      if (Number(r.giftExtraPhotos) > 0) {
        out.photoshootExtra += 1
      } else {
        out.photoshoot += 1
      }
    } else if (r.serviceType === 'carpool') {
      const variant = String(r.variant || '')
      const base = Number(r.basePriceCents) || 0
      const credit = Number(r.giftCreditCents) || 0
      if (variant === 'mco-roundtrip' && base > 0 && credit >= base) {
        out.carpool += 2
      } else {
        out.carpool += 1
      }
    }
  }

  return out
}

/**
 * Whether any gift unit has been redeemed.
 * @param {Record<string, number>} redemptions
 */
export function hasAnyGiftRedemption (redemptions) {
  const r = redemptions || {}
  return GIFT_KINDS.some(k => (r[k] || 0) > 0)
}

/**
 * Human reason for a gift kind lock.
 * @param {string} kind
 */
export function giftRedemptionReason (kind) {
  return REDEMPTION_REASONS[kind] || 'Already used'
}

/**
 * Assign locks left-to-right: the first N selections of each kind that cover
 * redeemed units are locked.
 *
 * @param {string[]} slots
 * @param {Record<string, number>} redemptions
 * @returns {Array<{ locked: boolean, kind: string|null, reason: string }>}
 */
export function lockFlagsForGiftSlots (slots, redemptions) {
  const remaining = { ...EMPTY_REDEMPTIONS(), ...(redemptions || {}) }
  return (slots || []).map(label => {
    const kind = giftKindFromLabel(label)
    if (!kind || !(remaining[kind] > 0)) {
      return { locked: false, kind: null, reason: '' }
    }
    const units = giftUnitsFromLabel(label)
    remaining[kind] = Math.max(0, remaining[kind] - units)
    return {
      locked: true,
      kind,
      reason: giftRedemptionReason(kind)
    }
  })
}

/**
 * Ensure proposed gift slots still cover all redeemed units.
 * @param {string[]} slots
 * @param {Record<string, number>} redemptions
 * @returns {string|null} error message or null if ok
 */
export function validateGiftSelectionsAgainstRedemptions (slots, redemptions) {
  const counts = countGiftUnitsByKind(slots)
  const r = redemptions || {}
  for (const kind of GIFT_KINDS) {
    const need = r[kind] || 0
    if (need > (counts[kind] || 0)) {
      return REDEMPTION_BLOCK_MESSAGES[kind] ||
        'That gift is already in use and can’t be removed. Email hello@iabf.dance for help.'
    }
  }
  return null
}
